/* NavRail — organism
 *
 * Port of design bundle's navrail.js + navrail.css.
 * Reference: src/design-system/_spec/project/design-system/molecules/navrail/
 *
 * Design vocabulary called this a "molecule"; in our system it lives at the
 * organism layer because it owns layout state (collapse) and composes the
 * whole left-rail surface (brand + nav + notifications + user).
 *
 * The component is *headless* — it does not know about routing, permissions,
 * users, or notifications. The consumer (e.g. main-layout/sidebar.tsx) wires
 * those in via props. This keeps the organism reusable and testable.
 *
 * Visual rules (collapsing, hover-expand glyph, tooltips) live in
 * ./nav-rail.css — restyle there, not here.
 */

import {
  useEffect,
  useState,
  type ComponentType,
  type ReactElement,
  type ReactNode,
} from "react";
import {
  Bell,
  ChevronsUpDown,
  PanelLeftClose,
  PanelLeftOpen,
} from "lucide-react";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import "./nav-rail.css";

/** Icon component contract — works with any lucide-react icon or anything
 *  that accepts a `size` prop and renders an SVG. */
export type NavRailIcon = ComponentType<{ size?: number; className?: string }>;

/** Anchor-like component used for nav items with an `href`. Defaults to a
 *  plain `<a>`. Pass a router-aware wrapper (e.g. react-router-dom's Link)
 *  to get SPA navigation while keeping the semantic `<a>` element. */
export type NavRailLinkComponent = ComponentType<{
  href: string;
  className?: string;
  children?: ReactNode;
  "data-nav"?: string;
  "aria-current"?: "page" | "step" | "location" | boolean;
  onClick?: (e: React.MouseEvent<HTMLAnchorElement>) => void;
}>;

export type NavRailItem = {
  /** Stable id used for the active-state lookup and React keys. */
  id: string;
  label: string;
  icon: NavRailIcon;
  /** Pass an href for client-side links (component renders an `<a>`). */
  href?: string | null;
  /** Or pass onClick for button behavior. If neither href nor onClick is
   *  provided, the item renders as disabled (Coming soon). */
  onClick?: () => void;
  /** Small numeric or text badge rendered to the right of the label.
   *  When the rail is collapsed it becomes a tiny red dot indicator. */
  badge?: number | string;
  /**
   * Renders the item indented, as a child of the section label above it.
   *
   * **Local addition, not in 8loop's copy of this component.** This app's rail has
   * to distinguish "Accounts" (the whole app) from the three pages that only exist
   * *inside* one account. Without it those four read as four peers, and an FDE
   * cannot tell that "Callback queue" means "this account's queue".
   */
  nested?: boolean;
};

/**
 * A label that groups the items after it.
 *
 * **Local addition, not in 8loop's copy.** Collapsed, there is no room for text, so
 * it renders as a plain divider — the grouping survives as a visual break.
 */
export type NavRailSection = {
  kind: "section";
  /** Stable key. */
  id: string;
  label: string;
  /** Optional second line, e.g. the tenant host under the account name. */
  sublabel?: string;
};

export type NavRailEntry = NavRailItem | NavRailSection;

function isSection(entry: NavRailEntry): entry is NavRailSection {
  return (entry as NavRailSection).kind === "section";
}

export type NavRailUser = {
  name: string;
  email: string;
  /** Two-letter initials shown in the avatar circle. Defaults to the first
   *  two characters of `name`. */
  initials?: string;
};

export type NavRailNotification = {
  /** Shows the small red dot indicator when true. */
  unread?: boolean;
  onClick?: () => void;
  label?: string;
};

export type NavRailProps = {
  /** Items, optionally interleaved with `{ kind: "section" }` group labels. */
  items: NavRailEntry[];
  bottomItems?: NavRailItem[];
  /** Marks the item with this id as active. */
  activeId?: string;

  user?: NavRailUser;
  onUserClick?: () => void;

  /** Wordmark shown in expanded state. Optional — if omitted the brand row
   *  collapses to icon-only. */
  brandWordmarkSrc?: string;
  /** Brand glyph shown in collapsed state. Hover reveals the expand icon. */
  brandGlyphSrc?: string;
  /** Optional click handler for the brand row when expanded (e.g. navigate
   *  to home). The collapsed brand row always expands the rail on click. */
  onBrandClick?: () => void;

  notification?: NavRailNotification;

  /** Initial collapsed state if there's no stored preference yet. */
  defaultCollapsed?: boolean;
  /** localStorage key used to persist collapse state. Pass `null` to disable
   *  persistence (useful in Storybook + tests). */
  storageKey?: string | null;

  /** aria-label for the rail element. Defaults to "Main navigation". */
  ariaLabel?: string;

  /** Custom slot rendered at the very bottom of the rail, below the user
   *  block. Use for popovers / drawers wired to user actions. */
  userSlot?: ReactNode;

  /** Component used to render link items. Defaults to a plain `<a>`. Pass a
   *  router-aware wrapper to enable SPA navigation. */
  linkComponent?: NavRailLinkComponent;
};

const DEFAULT_STORAGE_KEY = "8loop:navrail:collapsed";

function readStoredCollapsed(
  storageKey: string | null,
  fallback: boolean
): boolean {
  if (storageKey === null || typeof window === "undefined") return fallback;
  const stored = window.localStorage.getItem(storageKey);
  if (stored === null) return fallback;
  return stored === "1";
}

function writeStoredCollapsed(
  storageKey: string | null,
  collapsed: boolean
): void {
  if (storageKey === null || typeof window === "undefined") return;
  window.localStorage.setItem(storageKey, collapsed ? "1" : "0");
}

function deriveInitials(name: string): string {
  const parts = name.trim().split(/\s+/);
  if (parts.length >= 2) {
    return (parts[0]![0]! + parts[1]![0]!).toUpperCase();
  }
  return name.slice(0, 2).toUpperCase();
}

export function NavRail({
  items,
  bottomItems = [],
  activeId,
  user,
  onUserClick,
  brandWordmarkSrc,
  brandGlyphSrc,
  onBrandClick,
  notification,
  defaultCollapsed = false,
  storageKey = DEFAULT_STORAGE_KEY,
  ariaLabel = "Main navigation",
  userSlot,
  linkComponent,
}: NavRailProps) {
  const [collapsed, setCollapsed] = useState<boolean>(() =>
    readStoredCollapsed(storageKey, defaultCollapsed)
  );

  useEffect(() => {
    writeStoredCollapsed(storageKey, collapsed);
  }, [collapsed, storageKey]);

  const handleBrandClick = (e: React.MouseEvent<HTMLButtonElement>) => {
    if (collapsed) {
      e.preventDefault();
      setCollapsed(false);
      return;
    }
    onBrandClick?.();
  };

  const handleCollapseToggle = () => {
    setCollapsed((c) => !c);
  };

  return (
    <aside
      className={`rail${collapsed ? " collapsed" : ""}`}
      aria-label={ariaLabel}
    >
      <div className="rail-top">
        <button
          type="button"
          className="rail-brand"
          onClick={handleBrandClick}
          data-brand
          aria-label={collapsed ? "Expand navigation" : "Home"}
        >
          {brandWordmarkSrc && (
            <img className="wm" src={brandWordmarkSrc} alt="8loop" />
          )}
          {brandGlyphSrc && (
            <img className="app-icon" src={brandGlyphSrc} alt="8loop" />
          )}
          <span className="expand-glyph" aria-hidden="true">
            <PanelLeftOpen size={14} />
          </span>
        </button>
        <button
          type="button"
          className="rail-collapse-btn"
          onClick={handleCollapseToggle}
          aria-label={collapsed ? "Expand sidebar" : "Collapse sidebar"}
        >
          <PanelLeftClose size={14} />
        </button>
      </div>

      <nav className="rail-nav">
        {items.map((entry) =>
          isSection(entry) ? (
            <div key={entry.id} className="rail-section">
              {/* Collapsed, CSS hides the text and leaves the rule, so the group
                  break is still legible with no room for a label. */}
              <span className="rail-section-rule" aria-hidden="true" />
              <span className="rail-section-text">
                <span className="rail-section-label">{entry.label}</span>
                {entry.sublabel && (
                  <span className="rail-section-sublabel">{entry.sublabel}</span>
                )}
              </span>
            </div>
          ) : (
            <NavRailItemView
              key={entry.id}
              item={entry}
              activeId={activeId}
              collapsed={collapsed}
              linkComponent={linkComponent}
            />
          ),
        )}
      </nav>

      <div className="rail-bottom">
        {bottomItems.map((item) => (
          <NavRailItemView
            key={item.id}
            item={item}
            activeId={activeId}
            collapsed={collapsed}
            linkComponent={linkComponent}
          />
        ))}

        {notification && (
          <NavTooltip
            show={collapsed}
            tip={notification.label ?? "Notifications"}
          >
            <button
              type="button"
              className="rail-notif"
              onClick={notification.onClick}
              aria-label={notification.label ?? "Notifications"}
            >
              <Bell className="ico" size={16} />
              <span className="label">
                {notification.label ?? "Notifications"}
              </span>
              {notification.unread && (
                <span className="dot" aria-hidden="true" />
              )}
            </button>
          </NavTooltip>
        )}

        {user &&
          (userSlot ?? (
            <button
              type="button"
              className="rail-user"
              onClick={onUserClick}
              aria-label="User profile"
            >
              <div className="av" aria-hidden="true">
                {user.initials ?? deriveInitials(user.name)}
              </div>
              <div className="info">
                <span className="name">{user.name}</span>
                <span className="email">{user.email}</span>
              </div>
              <ChevronsUpDown className="chev" size={14} />
            </button>
          ))}
      </div>
    </aside>
  );
}

function NavTooltip({
  show,
  tip,
  children,
}: {
  show: boolean;
  tip: string;
  children: ReactElement;
}) {
  if (!show) return children;
  return (
    <Tooltip>
      <TooltipTrigger asChild>{children}</TooltipTrigger>
      <TooltipContent side="right" sideOffset={12}>
        {tip}
      </TooltipContent>
    </Tooltip>
  );
}

function NavRailItemView({
  item,
  activeId,
  collapsed,
  linkComponent: LinkComponent,
}: {
  item: NavRailItem;
  activeId?: string;
  collapsed: boolean;
  linkComponent?: NavRailLinkComponent;
}) {
  const isActive = item.id === activeId;
  const isDisabled = !item.href && !item.onClick;
  const Icon = item.icon;
  const tip = isDisabled ? `${item.label} · Coming soon` : item.label;
  const cls = [
    "nav-item",
    isActive && "active",
    isDisabled && "disabled",
    item.nested && "nested",
  ]
    .filter(Boolean)
    .join(" ");

  const content = (
    <>
      <Icon className="ico" size={16} />
      <span className="label">{item.label}</span>
      {item.badge !== undefined && <span className="badge">{item.badge}</span>}
      {isDisabled && <span className="soon-dot" aria-hidden="true" />}
    </>
  );

  let element: ReactElement;

  if (item.onClick) {
    element = (
      <button
        type="button"
        className={cls}
        onClick={item.onClick}
        data-nav={item.id}
      >
        {content}
      </button>
    );
  } else if (item.href) {
    if (LinkComponent) {
      element = (
        <LinkComponent
          className={cls}
          href={item.href}
          data-nav={item.id}
          aria-current={isActive ? "page" : undefined}
        >
          {content}
        </LinkComponent>
      );
    } else {
      element = (
        <a
          className={cls}
          href={item.href}
          data-nav={item.id}
          aria-current={isActive ? "page" : undefined}
        >
          {content}
        </a>
      );
    }
  } else {
    element = (
      <span className={cls} data-nav={item.id} aria-disabled="true">
        {content}
      </span>
    );
  }

  return (
    <NavTooltip show={collapsed} tip={tip}>
      {element}
    </NavTooltip>
  );
}
